import React, { useState, useCallback } from 'react';
import propTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Columns,
  Column,
  Container,
  Navbar,
  NavbarBrand,
  NavbarItem,
  NavbarMenu,
  NavbarEnd,
} from 'src/ui/mui-ui-kit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { findIndex } from 'lodash';
import { signOut } from 'src/auth';
import { getUser } from 'src/user';
import solventLogoPurpLong from 'src/ui/assets/solvent-logo-purp-long.png';
import solventLogoGrey from 'src/ui/assets/solvent-logo-grey.svg';

import {
  Stepper,
  StepOverview,
  StepProcess,
  StepNda,
  StepBegin,
} from '../components';

const stepNDA = {
  label: 'NDA',
  tag: 'nda',
  component: StepNda,
  prev: '/wizard/process',
  next: '/wizard/begin',
};

const employeeStepNDA = {
  label: 'BG Consent',
  tag: 'nda',
  component: StepNda,
  prev: '/wizard/process',
  next: '/wizard/begin',
};

const steps = [
  {
    label: 'Overview',
    tag: 'overview',
    component: StepOverview,
    next: '/wizard/process',
  },
  {
    label: 'Process',
    tag: 'process',
    component: StepProcess,
    prev: '/wizard/overview',
    next: '/wizard/nda',
  },
  { ...stepNDA },
  // {
  //   label: 'Contributors',
  //   tag: 'contributors',
  //   component: StepContributors,
  //   prev: '/wizard/nda',
  //   next: '/wizard/begin',
  // },
  { label: 'Begin', tag: 'begin', component: StepBegin, prev: '/wizard/nda' },
];

const employeeSteps = [
  {
    label: 'Overview',
    tag: 'overview',
    component: StepOverview,
    next: '/wizard/process',
  },
  {
    label: 'Process',
    tag: 'process',
    component: StepProcess,
    prev: '/wizard/overview',
    next: '/wizard/nda',
  },
  { ...employeeStepNDA },
  { label: 'Begin', tag: 'begin', component: StepBegin, prev: '/wizard/begin' },
];
function OnboardingWizardPage({ signOut, user }) {
  const { step } = useParams();
  const navigate = useNavigate();
  // Initialize state at the top level, following React Hook rules
  const [activeStep, setActiveStep] = useState(0);

  // Process step parameter early
  const processedStep = React.useMemo(() => {
    const matchedStep = step;

    if (matchedStep === 'nda-signed' || matchedStep === 'bg-signed') {
      return 'nda';
    }

    return matchedStep || 'overview';
  }, [step]);

  // Get step index based on user type - always define this hook
  const getStepIndex = useCallback(
    tag => {
      if (user.isMerchantAdmin) {
        return findIndex(steps, { tag });
      }
      return findIndex(employeeSteps, { tag });
    },
    [user.isMerchantAdmin]
  );

  // Calculate matched step index - always define this hook
  const matchedStepIndex = React.useMemo(() => {
    return getStepIndex(processedStep);
  }, [getStepIndex, processedStep]);

  // Update activeStep when matchedStepIndex changes
  React.useEffect(() => {
    if (activeStep !== matchedStepIndex) {
      setActiveStep(matchedStepIndex);
    }
  }, [matchedStepIndex, activeStep]);

  // Navigate to previous step - always define this hook
  const onPrevStep = useCallback(() => {
    const currentSteps = user.isMerchantAdmin ? steps : employeeSteps;
    const currentStep = currentSteps[activeStep];

    if (currentStep && currentStep.prev) {
      navigate(currentStep.prev);
      setActiveStep(activeStep - 1);
    }
  }, [activeStep, navigate, user.isMerchantAdmin]);

  // Navigate to next step - always define this hook
  const onNextStep = useCallback(() => {
    const currentSteps = user.isMerchantAdmin ? steps : employeeSteps;
    const currentStep = currentSteps[activeStep];

    if (currentStep && currentStep.next) {
      navigate(currentStep.next);
      setActiveStep(activeStep + 1);
    }
  }, [activeStep, navigate, user.isMerchantAdmin]);

  // Handle redirect for missing step
  const shouldRedirect = !step;
  if (shouldRedirect) {
    return <Navigate to="/wizard/overview" replace />;
  }

  return (
    <div className="onboarding-wizard-page has-background-white-ter">
      <Navbar>
        <Container>
          <div className="wrapper">
            <NavbarBrand>
              <NavbarItem>
                <img
                  src={
                    user.financialInstitutionLogoBlue ||
                    solventLogoPurpLong
                  }
                  alt="Bulldog FCU Logo"
                  width="131"
                />
              </NavbarItem>
            </NavbarBrand>
            <NavbarMenu>
              <NavbarEnd>
                <NavbarItem
                  tag="a"
                  className="has-text-grey-medium-dark"
                  onClick={signOut}
                >
                  <span className="has-text-weight-semibold">Logout</span>
                  <span className="icon">
                    <FontAwesomeIcon icon={['fal', 'sign-out']} />
                  </span>
                </NavbarItem>
              </NavbarEnd>
            </NavbarMenu>
          </div>
        </Container>
      </Navbar>
      <div className="stepper-wrapper">
        <Stepper
          activeStep={activeStep}
          steps={user.isMerchantAdmin ? steps : employeeSteps}
          onNextStep={onNextStep}
          onPrevStep={onPrevStep}
        />
      </div>
      <footer className="wizard-footer is-size-7 has-text-grey-medium-dark">
        <div className="container">
          <div className="wrapper">
            <Columns>
              <Column>
                <p>
                  <span className="icon">
                    <FontAwesomeIcon icon={['fal', 'copyright']} />
                  </span>
                  {/* eslint-disable jsx-a11y/anchor-is-valid */}
                  Copyright Solvent 2019 |{' '}
                  <a href="#">Terms &amp; Conditions</a> |{' '}
                  <a href="#">Privacy Policy</a>
                  {/* eslint-enable */}
                </p>
              </Column>
              <Column hasTextAlign="right">
                <p className="powered-wrapper">
                  Powered by
                  <img
                    src={solventLogoGrey}
                    alt="Solvent Logo"
                  />
                </p>
              </Column>
            </Columns>
          </div>
        </div>
      </footer>
    </div>
  );
}

OnboardingWizardPage.propTypes = {
  signOut: propTypes.func.isRequired,
  user: propTypes.object.isRequired,
};

function mapStateToProps(state) {
  return {
    user: getUser(state),
  };
}

const connectedOnboardingWizardPage = connect(mapStateToProps, {
  signOut,
})(OnboardingWizardPage);

export { connectedOnboardingWizardPage as OnboardingWizardPage };
